import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RequirementCategoryResponse, UserItemResponse } from '../../../api';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { baseAdministrationActions, baseCustomerDepartmentActions, baseSharedActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors } from '../selectors';
import { catchError, map, mergeMap, switchMap, withLatestFrom, tap, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { BaseCustomerDepartmentService } from '../../modules/customer-department/base-customer-department.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class BaseCustomerDepartmentEffects {
  loadCustomerDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerDepartment),
      switchMap((action) => {
        return this.departmentService.getCustomerDepartment(action.payload).pipe(
          mergeMap((response) => [
            baseCustomerDepartmentActions.setCustomerDepartment({ payload: response.data }),
            baseCustomerDepartmentActions.successCustomerDepartment(),
          ]),
          catchError(() => of(baseCustomerDepartmentActions.errorCustomerDepartment()))
        );
      })
    );
  });

  loadCustomerUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerUsers),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getCustomerUsers({
            customerId: user.organizationId,
            roles: action.payload?.roles,
            active: action.payload?.active,
            search: action.payload?.search,
          })
          .pipe(
            mergeMap((response: UserItemResponse[]) => [
              baseCustomerDepartmentActions.setCustomerUsers({ payload: response }),
              baseCustomerDepartmentActions.successCustomerUsers(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorCustomerUsers()))
          );
      })
    );
  });

  loadCustomerActiveUserList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerActiveUserList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const payload = {
          customerId: user.organizationId,
          search: action.payload?.search as string,
        };

        return this.departmentService
          .getCustomerUsers({ customerId: payload.customerId, search: payload.search, active: true, roles: ['user'] })
          .pipe(
            mergeMap((response: any) => [
              baseCustomerDepartmentActions.setCustomerActiveUserList({ payload: response }),
              baseCustomerDepartmentActions.successCustomerActiveUserList(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorCustomerActiveUserList()))
          );
      })
    );
  });

  updateUsersToCustomerDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateUsersToCustomerDepartment),
      switchMap((action) => {
        return this.departmentService.updateUsersToCustomerDepartment(action.payload).pipe(
          map(() =>
            baseAdministrationActions.loadLocationDepartmentsData({
              locationId: action.payload.location,
            })
          )
        );
      })
    );
  });

  updateCustomerDepartmentMasterData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentMasterData),
      switchMap((action) => {
        return this.departmentService.updateMasterData(action.payload).pipe(
          map(() =>
            baseAdministrationActions.loadLocationDepartmentsData({
              locationId: action.payload.location,
              search: action.payload.search,
            })
          )
        );
      })
    );
  });

  updateCustomerDepartmentShiftTimes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentShiftTimes),
      switchMap((action) => {
        return this.departmentService.updateShiftTimes(action.payload).pipe(
          mergeMap(() => [
            baseCustomerDepartmentActions.successUpdateCustomerDepartmentShiftTimes(),
            baseCustomerDepartmentActions.loadCustomerDepartment({
              payload: {
                department: action.payload.department,
                customer: action.payload.customer,
              },
            }),
          ]),
          catchError(() => of(baseCustomerDepartmentActions.errorUpdateCustomerDepartmentShiftTimes()))
        );
      })
    );
  });

  loadRequirements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadRequirementCategories),
      switchMap((action) => {
        return this.departmentService.getAvailableRequirementCategories(action.payload?.tag).pipe(
          mergeMap((categories: RequirementCategoryResponse[]) => {
            let requirements: any[] = [];
            categories.forEach((category) => {
              requirements = [...requirements, ...category.requirements];
            });
            return [
              baseCustomerDepartmentActions.setRequirementCategories({ payload: requirements }),
              baseCustomerDepartmentActions.successRequirementCategories(),
            ];
          }),
          catchError(() => of(baseCustomerDepartmentActions.errorRequirementCategories()))
        );
      })
    );
  });

  updateCustomerDepartmentRequirements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentRequirements),
      switchMap((action) => {
        return this.departmentService.updateRequirements(action.payload).pipe(
          mergeMap(() => [
            baseCustomerDepartmentActions.successUpdateCustomerDepartmentRequirements(),
            baseCustomerDepartmentActions.loadCustomerDepartment({
              payload: {
                department: action.payload.department,
                customer: action.payload.customer,
              },
            }),
          ]),
          catchError(() => of(baseCustomerDepartmentActions.errorUpdateCustomerDepartmentRequirements()))
        );
      })
    );
  });

  deleteCustomerDepartmentRequirement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.deleteCustomerDepartmentRequirement),
      concatMap((action) => {
        return this.departmentService.deleteRequirement(action.payload).pipe(
          mergeMap(() => [baseCustomerDepartmentActions.successDeleteCustomerDepartmentRequirement()]),
          catchError(() => of(baseCustomerDepartmentActions.errorDeleteCustomerDepartmentRequirement()))
        );
      })
    );
  });

  createDepartmentCrud$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.createDepartmentCrud),
      switchMap((action) => {
        return this.departmentService
          .createDepartment(action.payload.customer, action.payload.location, action.payload.body)
          .pipe(
            mergeMap((createdDepartment) => [
              baseCustomerDepartmentActions.setDepartmentCrud({ payload: createdDepartment }),
              baseAdministrationActions.loadLocationsData({ payload: { search: action.payload.search } }),
              baseCustomerDepartmentActions.successDepartmentCrud(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorDepartmentCrud()))
          );
      })
    );
  });

  addFavouriteQualifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.addFavouriteQualifications),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .addFavouriteQualifications({
            customer: user.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap(() => [
              baseCustomerDepartmentActions.addFavouriteQualificationsSuccess(),
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'favorite.success',
                },
              }),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.addFavouriteQualificationsError()))
          );
      })
    );
  });

  deleteFavouriteQualification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.deleteFavouriteQualification),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .deleteFavouriteQualification({
            customer: user.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            qualification: action.payload.qualification,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap(() => [
              baseCustomerDepartmentActions.deleteFavouriteQualificationSuccess(),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.deleteFavouriteQualificationError()))
          );
      })
    );
  });

  updateDepartmentQualificationDescription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateDepartmentQualificationDescription),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .updateDepartmentQualificationDescription({
            qualification: action.payload.qualification,
            customer: user?.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseSharedActions.loadCustomerDepartmentQualificationsList({
                payload: {
                  department: action.payload.department,
                },
              }),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ])
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private translateService: TranslateService,
    private readonly departmentService: BaseCustomerDepartmentService
  ) {}
}
